import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SearchNormal1 } from "iconsax-react";
import useSticky from "../Hooks/useSticky";
import { FoodCard } from "./foodCard";
import "./style-rtl.scss";
import "./style.scss";
import { Loading } from "./skeleton-loading/cartLoading";
import { LoadingMenu } from "./skeleton-loading/menuLoading";
import { get } from "../../controlers/global/ConfigRequest";
import FoodModal from "./foodModal";
import { GET_ALL_FOOD_ITEM_TYPES } from "../universalData";
import { GlobalStateContext } from "../../layouts/GlobalStates";
import { numberWithCommas } from "../../utils/methods";

export default function FoodSection() {
    const { feedbackAppGlobalState } = useContext(GlobalStateContext);
    const [result] = feedbackAppGlobalState;
    const [activeTab, setActiveTab] = useState({});
    const [basketStatus, setBasketStatus] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [menu, setMenu] = useState([]);
    const [basket, setBasket] = useState([]);
    const [search, setSearch] = useState("");
    const { t } = useTranslation();

    useEffect(() => {
        setIsLoading(true);
        get(GET_ALL_FOOD_ITEM_TYPES, { company: result.data.company })
            .then((menuItems) => {
                const rows = Array.isArray(menuItems) ? menuItems : [];
                setMenu(rows);
                setActiveTab(rows[0] || {});
            })
            .catch(() => {
                setMenu([]);
                setActiveTab({});
            })
            .finally(() => setIsLoading(false));
    }, [result.data.company]);

    useSticky([
        {
            query: ".navbar-menu",
            type: "height",
            min: 70,
            height: 50,
        },
        {
            reference: ".navbar-menu",
            min: 80,
            query: ".x",
            type: "top",
            offset: 40,
            target: ".name-rate",
        },
    ]);

    const query = search.trim().toLowerCase();
    const visibleItems = useMemo(() => {
        const match = (item) => {
            if (!query) return true;
            return (
                (item?.name || "").toLowerCase().includes(query) ||
                (item?.description || "").toLowerCase().includes(query)
            );
        };
        if (query) {
            return menu.flatMap((category) =>
                (category.item_set || []).filter(match)
            );
        }
        return activeTab?.item_set || [];
    }, [query, menu, activeTab]);

    const basketCount = basket.reduce((total, group) => total + group.length, 0);
    const basketTotal = basket.reduce((total, group) => {
        const price = Number(group[0]?.restaurant_sale_cards?.[0]?.price || 0);
        return total + price * group.length;
    }, 0);

    const setBasketItems = (next) => {
        setBasket(next);
    };

    return (
        <div className={`food-main${basketCount ? " food-main--cart" : ""}`}>
            <FoodModal
                data={basket}
                show={basketStatus}
                onClose={() => setBasketStatus(false)}
                companyId={result.data.company}
                onDelete={(index) => {
                    setBasket((current) =>
                        current.filter((_, idx) => idx !== index)
                    );
                }}
                onClearBasket={() => setBasket([])}
            />
            <label className="sales-search">
                <SearchNormal1 size={18} aria-hidden="true" />
                <input
                    type="search"
                    value={search}
                    placeholder={t("sales.search")}
                    onChange={(event) => setSearch(event.target.value)}
                />
            </label>
            <div className="navbar-menu">
                <ul className="x">
                    {isLoading ? (
                        <LoadingMenu />
                    ) : (
                        menu.map((element) => (
                            <li key={element.id || element.name}>
                                <button
                                    type="button"
                                    name={element.name}
                                    className={`tab-title${
                                        !query &&
                                        activeTab.name === element.name
                                            ? " active-tab"
                                            : ""
                                    }`}
                                    onClick={() => {
                                        setSearch("");
                                        setActiveTab(element);
                                    }}
                                >
                                    {element.name}
                                    <span>
                                        {(element.item_set || []).length}
                                    </span>
                                </button>
                            </li>
                        ))
                    )}
                </ul>
            </div>
            {isLoading ? (
                <Loading />
            ) : (
                <div className="foods">
                    {visibleItems.length === 0 ? (
                        <p className="sales-empty">
                            {query ? t("sales.noMatch") : t("sales.empty")}
                        </p>
                    ) : (
                        visibleItems.map((element) => (
                            <FoodCard
                                key={element.id}
                                card={element}
                                onSelectProduct={setBasketItems}
                                basketItems={basket}
                            />
                        ))
                    )}
                </div>
            )}
            {basketCount > 0 ? (
                <div className="sales-cart-bar">
                    <div className="sales-cart-bar__meta">
                        <strong>
                            {t("sales.cart.items", { count: basketCount })}
                        </strong>
                        <span>
                            {numberWithCommas(String(basketTotal))}{" "}
                            {t("currency")}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setBasketStatus(true)}
                    >
                        {t("sales.cart.continue")}
                    </button>
                </div>
            ) : null}
        </div>
    );
}
